import { useEffect, useState } from "react";
import { collection, doc, getDocs } from "firebase/firestore";
import { db } from "../../../../services/firebase.config";
import { DataGrid, GridActionsCellItem, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import { eventColumns } from "./helpers/EventColumns";
import { FaPlus, FaTimes, FaTrash, FaUpload } from "react-icons/fa";
import { Button, Grid, AlertTitle, Alert, Box, CircularProgress } from "@mui/material";
import { addDoc, setDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import Error from "../../../../components/Error/Error";
import { CustomInput, CustomLabel } from "./styles";
import { Event } from "../../../../models/event";
import DialogDelete from "../helpers/DialogDelete";
import DialogAdd from "./helpers/DialogAdd";

const fileReader = new FileReader();

const EventPart = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
    const [deleteAllEvents, setDeleteAllEvents] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);
    const [isOpenAdd, setIsOpenAdd] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchEvents = async () => {
        try {
            const data = await getDocs(collection(db, "events"));
            if (data.empty) {
                setEvents([]);
            } else {
                const events = (data.docs.map((doc) => {
                    const event = doc.data() as Event;
                    event.id = doc.id;
                    // Keep as Date objects for MUI X Data Grid v8 date columns
                    event.StartDate = doc.data()["StartDate"].toDate();
                    event.EndDate = doc.data()["EndDate"].toDate();
                    return event;
                }));
                setEvents(events);
            }
        } catch (error: any) {
            setError(error.message);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const getEventsFromFile = (fileAsString: String) => {
        try {
            setEvents([]);
            const csvRows = fileAsString.split(/\r\n?\n/);
            if (csvRows.length > 0) {
                csvRows.shift();
            }
            const dataOfCsv: Event[] = [];
            csvRows.forEach((row: string) => {
                if (row !== "") {
                    const rowValues = row.split(',');
                    const dateEnd = rowValues[3].split(' ')[0].split('/');
                    const timeEnd = rowValues[3].split(' ')[1].split(':');

                    const dateStart = rowValues[4].split(' ')[0].split('/');
                    const timeStart = rowValues[4].split(' ')[1].split(':');
                    dataOfCsv.push({
                        id: "",
                        Title: rowValues[0],
                        Description: rowValues[1],
                        Speaker: rowValues[2],
                        EndDate: new Date(parseInt(dateEnd[2]), parseInt(dateEnd[1]) - 1, parseInt(dateEnd[0]), parseInt(timeEnd[0]), parseInt(timeEnd[1])),
                        StartDate: new Date(parseInt(dateStart[2]), parseInt(dateStart[1]) - 1, parseInt(dateStart[0]), parseInt(timeStart[0]), parseInt(timeStart[1])),
                        Location: rowValues[5],
                    });
                }
            });
            if (dataOfCsv.length > 0) {
                let erreurInsertion = 0;
                let numberItems = 0;
                dataOfCsv.forEach(async (event: Event) => {
                    setIsLoading(true);
                    if (event.Title !== "") {
                        const idEventAdd = await addDoc(collection(db, "events"), event);
                        await setDoc(doc(db, "events", idEventAdd.id), { id: idEventAdd.id }, { merge: true });
                    }
                    else {
                        erreurInsertion++;
                    }
                    numberItems++;
                    if (numberItems === dataOfCsv.length) {
                        if (erreurInsertion === 0) {
                            toast.success("Les évènements ont bien été ajoutées", { position: "bottom-center", theme: "colored" });
                        } else {
                            toast.error(`Une erreur est survenue lors de l'insertion de ${erreurInsertion} évènement(s)`, { position: "bottom-center", theme: "colored" });
                        }
                        setIsLoading(false);
                        fetchEvents();
                    }
                });
            } else {
                toast.error("Le fichier CSV sélectionner ne contient aucun évènements", { position: "bottom-center", theme: "colored" });
            }
        } catch (error) {
            const csvRows = fileAsString.split(/\r\n?\n/);
            if (csvRows[0] !== "Titre,Description,Conférencier,Date de fin,Date de début,Localisation") {
                toast.error("Fichier CSV Incorrect", { position: "bottom-center", theme: "colored" });
            }
        }
    };

    const onSetCsvFile = (e: any) => {
        if (e?.target?.files[0]) {
            fileReader.onload = ({ target }) => getEventsFromFile(target?.result as String);
            fileReader.readAsText(e.target.files[0], 'ISO-8859-1');
        }
        // réinitialisation de l'event pour pouvoir recharger le même fichier
        e.target.value = '';
    };

    const getActions = (params: { row: Event }) => [
        <GridActionsCellItem
            key="answer"
            icon={<FaTrash />}
            label="Suppresssion d'un évènement"
            onClick={() => { setSelectedEvent(params.row); setIsOpenDelete(true); setDeleteAllEvents(false); }}
        />,
    ];

    const columns: GridColDef[] = [
        ...eventColumns,
        {
            field: 'Supprimer',
            type: 'actions',
            flex: 1,
            getActions,
        } as GridColDef,
    ];


    return (
        <>
            <Grid container mb={1} spacing={1} justifyContent={"flex-end"}>
                <Grid size={2} >
                    <Button
                        style={{ height: 55 }}
                        color="success"
                        size="large"
                        variant="contained"
                        startIcon={<FaPlus />}
                        onClick={() => setIsOpenAdd(true)}
                    >
                        Ajouter un évènement
                    </Button>
                </Grid>
                <Grid size={2}>
                    <CustomLabel htmlFor="upload-csv">
                        <CustomInput
                            type="file"
                            id="upload-csv"
                            accept=".csv"
                            onChange={onSetCsvFile}
                        />
                        <Button style={{ height: 55 }} color="primary" fullWidth variant="contained" component="span" aria-label="Sélectionner un fichier" startIcon={<FaUpload />}>
                            Ajouter un CSV
                        </Button>
                    </CustomLabel>
                </Grid>
                <Grid>
                    <Button
                        style={{ height: 55 }}
                        variant="contained"
                        color="error"
                        size="large"
                        disabled={events.length === 0}
                        onClick={() => { setDeleteAllEvents(true); setIsOpenDelete(true); }}
                    >
                        <FaTimes />
                    </Button>
                </Grid>
            </Grid>
            <DataGrid
                getRowId={(row: Event) => row.id}
                columns={columns}
                rows={events}
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        csvOptions: {
                            fileName: "Events",
                            utf8WithBom: true,
                        }
                    }
                }}
            />
            {!error ? (
                !isLoading ? (
                    events.length === 0 ? (
                        <Alert severity="info" style={{ marginTop: 10 }}>
                            <AlertTitle>Base de Données</AlertTitle>
                            Il n'y a aucun évènement en base de données
                        </Alert>
                    ) : null
                ) :
                    (
                        <Box justifyContent="center" alignItems="center" display="flex" width="100%" minHeight="25rem">
                            <CircularProgress />
                        </Box>
                    )
            ) : (
                <Error message={"Une erreur est survenue"} />
            )}
            <ToastContainer />
            <DialogDelete
                isOpen={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                typeObject="events"
                item={selectedEvent}
                allItems={deleteAllEvents}
                reloadDatas={fetchEvents}
            />
            <DialogAdd
                isOpen={isOpenAdd}
                onClose={() => setIsOpenAdd(false)}
                reloadDatas={fetchEvents}
            />
        </>

    );
};

export default EventPart;